/**
 * Build step 11 — the client deck (operator decision 19; prototype `VIEWS.deck`, reached from a client's page by
 * "Make client deck"). A short review deck built from what is already in Ops: tasks delivered, requests closed,
 * usage and open work. Decisions behind each choice: docs/architecture/step11-contract-decisions.md.
 *
 * REVENUE NEVER APPEARS IN A CLIENT DECK (prototype: "Revenue never appears in a client deck."). The deck goes to
 * the client, so the same body is served to every caller, managers included: there is no revenue audience and no
 * money field anywhere below. Every object is `.strict()`, so a stray money key fails validation instead of
 * reaching a slide, and a contract test walks the schema for money-like keys.
 */
import { z } from 'zod';
import { IsoDate, Percent, Timestamp, Uuid } from './common.js';
import { ClientRef } from './clients.js';
import { UserRef } from './users.js';
// ============================================================================ period and sections
/**
 * A calendar month `YYYY-MM` (a monthly review) or a calendar quarter `YYYY-Qn` (a quarterly review; Q3 = July to
 * September). The prototype offers "September 2026" and "Q3 2026". A period that has not started yet is refused (422);
 * the current one is allowed and covers up to today.
 */
export const DeckPeriod = z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2]|Q[1-4])$/, 'Use YYYY-MM for a month or YYYY-Qn for a quarter, for example 2026-09 or 2026-Q3')
    .meta({ id: 'DeckPeriod' });
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
/** "September 2026" / "Q3 2026" — the period select's wording and the cover's. */
export function deckPeriodLabel(p) {
    const [y, x] = p.split('-');
    return x.startsWith('Q') ? `${x} ${y}` : `${MONTHS[Number(x) - 1]} ${y}`;
}
/** First and last calendar day of a period (`YYYY-MM-DD`, inclusive). Shared by the API and tests. */
export function deckPeriodRange(p) {
    const [ys, x] = p.split('-');
    const y = Number(ys);
    const firstMonth = x.startsWith('Q') ? (Number(x.slice(1)) - 1) * 3 + 1 : Number(x);
    const lastMonth = x.startsWith('Q') ? firstMonth + 2 : firstMonth;
    const lastDay = new Date(Date.UTC(y, lastMonth, 0)).getUTCDate();
    const mm = (m) => String(m).padStart(2, '0');
    return { from: `${ys}-${mm(firstMonth)}-01`, to: `${ys}-${mm(lastMonth)}-${mm(lastDay)}` };
}
/** The four optional sections, in slide order (prototype "Sections"). The cover is always first and not a choice. */
export const DECK_SECTIONS = ['highlights', 'usage', 'delivered', 'next_month'];
export const DeckSection = z.enum(DECK_SECTIONS).meta({ id: 'DeckSection' });
/** Prototype checkbox and slide-label wording. */
export const DECK_SECTION_LABEL = {
    highlights: 'Highlights',
    usage: 'Usage',
    delivered: 'What we delivered',
    next_month: 'Next month',
};
/** A list slide shows at most this many lines (the prototype shows four); the rest is counted in `more`. */
export const DECK_LIST_MAX = 4;
// ============================================================================ slides
/** "Kestrel Bank" / "Monthly review, September 2026". */
export const DeckCoverSlide = z
    .object({ kind: z.literal('cover'), client_name: z.string(), subtitle: z.string() })
    .strict()
    .meta({ id: 'DeckCoverSlide' });
/**
 * Three numbers for the period (IST dates):
 * - `tasks_delivered`: the client's tasks marked done in the period (`tasks.done_at`), dropped ones excluded;
 * - `requests_closed`: the client's requests closed in the period (`client_requests.closed_at`);
 * - `on_time_pct`: of `tasks_delivered`, the share done on or before their due date, rounded; `null` when none.
 */
export const DeckHighlightsSlide = z
    .object({
    kind: z.literal('highlights'),
    tasks_delivered: z.number().int().nonnegative(),
    requests_closed: z.number().int().nonnegative(),
    on_time_pct: Percent.nullable(),
})
    .strict()
    .meta({ id: 'DeckHighlightsSlide' });
/**
 * Share of plan used, per active product (the usage hub's utilisation). A month: that month's figure, or month to
 * date for the current month. A quarter: Σ used / Σ plan over its months that have data. `null` = no usage data
 * ("n/a"), never zero. Only the percentage — never the plan's size, price or value.
 */
export const DeckUsageSlide = z
    .object({
    kind: z.literal('usage'),
    products: z.array(z
        .object({ product_key: z.string(), product_name: z.string(), util: z.number().min(0).max(400).nullable() })
        .strict()),
    /** "Share of plan used this month" / "… this quarter". */
    caption: z.string(),
})
    .strict()
    .meta({ id: 'DeckUsageSlide' });
/** One line on a list slide: a task title as it stands, with its date (done date, or due date for open work). */
export const DeckListItem = z
    .object({ task_id: Uuid, title: z.string(), date: IsoDate })
    .strict()
    .meta({ id: 'DeckListItem' });
/**
 * `delivered`: the client's tasks done in the period, latest first. `next_month`: the client's open tasks at
 * generation (not done, not dropped), soonest due first. At most `DECK_LIST_MAX` items; `more` counts the rest.
 * An empty list is shown as such — the deck never invents a line.
 */
export const DeckListSlide = z
    .object({
    kind: z.enum(['delivered', 'next_month']),
    items: z.array(DeckListItem).max(DECK_LIST_MAX),
    more: z.number().int().nonnegative(),
})
    .strict()
    .meta({ id: 'DeckListSlide' });
export const DeckSlide = z
    .union([DeckCoverSlide, DeckHighlightsSlide, DeckUsageSlide, DeckListSlide])
    .meta({ id: 'DeckSlide' });
// ============================================================================ the deck
/**
 * A generated deck: a snapshot taken at `generated_at`, so the preview and both downloads show the same numbers
 * even if the data moves on. Generating again makes a new deck.
 */
export const ClientDeck = z
    .object({
    id: Uuid,
    client: ClientRef,
    period: DeckPeriod,
    /** `deckPeriodLabel(period)`. */
    period_label: z.string(),
    sections: z.array(DeckSection),
    /** Cover first, then the chosen sections in `DECK_SECTIONS` order. */
    slides: z.array(DeckSlide).min(1),
    /** Last day the numbers cover: the period's end, or today (IST) for the current period. */
    data_to: IsoDate,
    generated_at: Timestamp,
    generated_by: UserRef,
})
    .strict()
    .meta({ id: 'ClientDeck' });
export const ClientDeckParams = z.object({ id: Uuid });
/** Generate: the period and at least one section (the prototype disables nothing, but a cover alone is no deck). */
export const CreateDeckBody = z
    .object({
    period: DeckPeriod,
    sections: z
        .array(DeckSection)
        .min(1, 'Pick at least one section')
        .refine((s) => new Set(s).size === s.length, 'Each section once'),
})
    .strict();
export const DECK_FORMATS = ['pdf', 'pptx'];
export const DeckFormat = z.enum(DECK_FORMATS).meta({ id: 'DeckFormat' });
export const DeckFileQuery = z.object({ format: DeckFormat });
//# sourceMappingURL=deck.js.map